import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { usePopup } from '@/context/PopupContext';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { toast } from 'sonner';
import { X } from 'lucide-react';
import { getGeoLocation, submitLead } from '@/services/api';

const formSchema = z.object({
  name: z.string()
    .min(2, 'Name must be at least 2 characters')
    .max(50, 'Name must not exceed 50 characters'),
  email: z.string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address (e.g., user@example.com)'),
  country_code: z.string(),
  contact_number: z.string()
    .min(10, 'Phone number must be exactly 10 digits after country code')
    .max(10, 'Phone number must be exactly 10 digits (no more than 10)')
    .regex(/^\d{10}$/, 'Phone number must contain exactly 10 digits only (alphabets and special characters are not allowed)'),
  message: z.string()
    .max(500, 'Message must not exceed 500 characters')
    .optional(),
});

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Service-specific popup content
const popupContent = {
  'trademark-registration': {
    image: 'https://images.unsplash.com/photo-1758520144427-ddb02ac74e9d?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    title: 'Protect Your Brand',
    subtitle: 'Start your trademark registration today'
  },
  'copyright-registration': {
    image: 'https://images.unsplash.com/photo-1758520144427-ddb02ac74e9d?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    title: 'Protect Your Creative Work',
    subtitle: 'Secure copyright for your original creations'
  },
  'patent-services': {
    image: 'https://images.unsplash.com/photo-1758520144427-ddb02ac74e9d?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    title: 'Protect Your Innovation',
    subtitle: 'Expert patent services for your inventions'
  },
  'company-formation': {
    image: 'https://images.unsplash.com/photo-1758520144427-ddb02ac74e9d?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    title: 'Start Your Business',
    subtitle: 'Professional company formation services'
  },
  'amazon-brand-registry': {
    image: 'https://images.unsplash.com/photo-1758520144427-ddb02ac74e9d?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    title: 'Protect Your Amazon Brand',
    subtitle: 'Get enrolled in Amazon Brand Registry'
  },
  'trademark-search': {
    image: 'https://images.unsplash.com/photo-1758520144427-ddb02ac74e9d?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    title: 'Search Your Trademark',
    subtitle: 'Comprehensive availability analysis'
  },
  'office-action-response': {
    image: 'https://images.unsplash.com/photo-1758520144427-ddb02ac74e9d?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    title: 'Overcome Office Actions',
    subtitle: 'Expert legal response services'
  },
  'trademark-renewal': {
    image: 'https://images.unsplash.com/photo-1758520144427-ddb02ac74e9d?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    title: 'Renew Your Trademark',
    subtitle: 'Never miss a renewal deadline'
  },
  'business-dissolution': {
    image: 'https://images.unsplash.com/photo-1758520144427-ddb02ac74e9d?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    title: 'Close Your Business Properly',
    subtitle: 'Professional dissolution services'
  },
  'legal-consultation': {
    image: 'https://images.unsplash.com/photo-1758520144427-ddb02ac74e9d?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    title: 'Expert Legal Advice',
    subtitle: 'Professional consultation services'
  },
  'home': {
    image: 'https://images.unsplash.com/photo-1758520144427-ddb02ac74e9d?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    title: 'Let\'s Protect Your Brand',
    subtitle: 'Get started with our expert team today'
  },
  'about': {
    image: 'https://images.unsplash.com/photo-1758520144427-ddb02ac74e9d?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    title: 'Work With Our Team',
    subtitle: 'Experience you can trust'
  },
  'landing': {
    image: 'https://images.unsplash.com/photo-1758520144427-ddb02ac74e9d?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    title: 'Get Started Today',
    subtitle: 'Professional trademark services'
  },
  'services': {
    image: 'https://images.unsplash.com/photo-1758520144427-ddb02ac74e9d?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    title: 'Get Started Today',
    subtitle: 'Professional services for your business'
  },
  'services-cta': {
    image: 'https://images.unsplash.com/photo-1758520144427-ddb02ac74e9d?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    title: 'Start Your Application',
    subtitle: 'Let us help protect your brand'
  },
  'default': {
    image: 'https://images.unsplash.com/photo-1758520144427-ddb02ac74e9d?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    title: 'Let\'s Protect Your Brand',
    subtitle: 'Get started with our expert team today'
  }
};

export const UniversalPopup = () => {
  const { isOpen, closePopup, popupContext } = usePopup();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [geoData, setGeoData] = useState(null);
  
  const { register, handleSubmit, formState: { errors }, reset, setValue } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      country_code: '+1'
    }
  });

  // Fetch geolocation data when popup opens
  useEffect(() => {
    if (isOpen) {
      const fetchGeoData = async () => {
        const data = await getGeoLocation();
        setGeoData(data);
        
        // Auto-select country code based on geolocation
        if (data?.data?.countryCode) {
          const countryCodeMap = {
            'US': '+1', 'GB': '+44', 'IN': '+91', 'AU': '+61', 'CN': '+86',
            'JP': '+81', 'DE': '+49', 'FR': '+33', 'IT': '+39', 'ES': '+34',
            'RU': '+7', 'BR': '+55', 'MX': '+52', 'KR': '+82', 'NL': '+31',
            'SE': '+46', 'CH': '+41', 'NO': '+47', 'DK': '+45', 'PL': '+48',
            'SG': '+65', 'MY': '+60', 'TH': '+66', 'VN': '+84', 'ID': '+62',
            'PH': '+63', 'PK': '+92', 'BD': '+880', 'AE': '+971', 'SA': '+966',
            'ZA': '+27', 'NG': '+234', 'EG': '+20', 'KE': '+254', 'NZ': '+64',
            'IE': '+353', 'PT': '+351', 'GR': '+30', 'BE': '+32', 'AT': '+43', 'FI': '+358'
          };
          const dialCode = countryCodeMap[data.data.countryCode];
          if (dialCode) {
            setValue('country_code', dialCode);
          }
        }
      };
      
      fetchGeoData();
    }
  }, [isOpen, setValue]);

  const content = popupContent[popupContext] || popupContent.default;

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const result = await submitLead(data, geoData);
      
      // Handle 200 Success - Redirect to application page
      if (result.success && result.statusCode === 200 && result.customerId) {
        toast.success('Form submitted successfully! Redirecting...');
        reset();
        closePopup();
        // Use window.location.href for full page reload
        window.location.href = `/application/main?customerid=${result.customerId}`;
      } 
      // Handle 401 Unauthorized
      else if (result.statusCode === 401) {
        toast.error(result.message || 'Domain not configured. Please contact support.');
      }
      // Handle 400 Bad Request
      else if (result.statusCode === 400) {
        toast.error(result.message || 'Invalid data provided. Please check your information.');
      }
      // Handle other errors
      else {
        toast.error(result.message || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
      console.error('Submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={closePopup}>
      <DialogContent className="max-w-[95%] sm:max-w-md md:max-w-2xl lg:max-w-4xl p-0 overflow-hidden bg-white border-2 border-gray-200 max-h-[90vh] overflow-y-auto">
        {/* Close button - Explicitly styled for visibility */}
        <button
          onClick={closePopup}
          className="absolute right-2 top-2 sm:right-4 sm:top-4 z-50 rounded-sm opacity-70 ring-offset-white transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-gray-100 bg-white p-1.5 sm:p-2 shadow-lg"
          aria-label="Close"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-4 w-4 sm:h-5 sm:w-5 text-gray-700"
          >
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
        
        <div className="grid md:grid-cols-2 gap-0 bg-white">
          {/* Left Side - Dynamic Image based on context */}
          <div className="hidden md:block relative">
            <img
              src={content.image}
              alt="Contact us"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-brand-navy/90 to-brand-navy/80" />
            <div className="absolute inset-0 flex items-center justify-center p-8">
              <div className="text-white text-center">
                <h3 className="text-3xl font-heading font-bold mb-4 drop-shadow-lg">
                  {content.title}
                </h3>
                <p className="text-lg text-gray-100 drop-shadow-md">
                  {content.subtitle}
                </p>
              </div>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="p-4 sm:p-6 md:p-8 bg-white">
            <div className="mb-4 sm:mb-6">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-heading font-bold text-gray-900 mb-2 sm:mb-3">
                Get Started Today
              </h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                Fill out the form and we'll contact you within 24 hours.
              </p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-3 sm:space-y-4">
              <div>
                <Label htmlFor="name" className="text-brand-navy text-xs sm:text-sm">Name</Label>
                <Input
                  id="name"
                  data-testid="popup-name-input"
                  placeholder="Your full name"
                  {...register('name')}
                  className="mt-1 text-gray-900 placeholder:text-gray-400 text-sm"
                />
                {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name.message}</p>}
              </div>
              
              <div>
                <Label htmlFor="email" className="text-brand-navy text-xs sm:text-sm">Email</Label>
                <Input
                  id="email"
                  data-testid="popup-email-input"
                  type="email"
                  placeholder="your@email.com"
                  {...register('email')}
                  className="mt-1 text-gray-900 placeholder:text-gray-400 text-sm"
                />
                {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email.message}</p>}
              </div>
              
              <div>
                <Label htmlFor="contact" className="text-brand-navy text-xs sm:text-sm">Contact Number</Label>
                <div className="flex gap-2 mt-1">
                  <Select defaultValue="+1" onValueChange={(value) => setValue('country_code', value)}>
                    <SelectTrigger className="w-20 sm:w-28 text-gray-900 text-sm" data-testid="popup-country-code-select">
                      <SelectValue className="text-gray-900" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="+1">🇺🇸 +1</SelectItem>
                      <SelectItem value="+44">🇬🇧 +44</SelectItem>
                      <SelectItem value="+91">🇮🇳 +91</SelectItem>
                      <SelectItem value="+61">🇦🇺 +61</SelectItem>
                      <SelectItem value="+86">🇨🇳 +86</SelectItem>
                      <SelectItem value="+81">🇯🇵 +81</SelectItem>
                      <SelectItem value="+49">🇩🇪 +49</SelectItem>
                      <SelectItem value="+33">🇫🇷 +33</SelectItem>
                      <SelectItem value="+39">🇮🇹 +39</SelectItem>
                      <SelectItem value="+34">🇪🇸 +34</SelectItem>
                      <SelectItem value="+7">🇷🇺 +7</SelectItem>
                      <SelectItem value="+55">🇧🇷 +55</SelectItem>
                      <SelectItem value="+52">🇲🇽 +52</SelectItem>
                      <SelectItem value="+82">🇰🇷 +82</SelectItem>
                      <SelectItem value="+31">🇳🇱 +31</SelectItem>
                      <SelectItem value="+46">🇸🇪 +46</SelectItem>
                      <SelectItem value="+41">🇨🇭 +41</SelectItem>
                      <SelectItem value="+47">🇳🇴 +47</SelectItem>
                      <SelectItem value="+45">🇩🇰 +45</SelectItem>
                      <SelectItem value="+48">🇵🇱 +48</SelectItem>
                      <SelectItem value="+65">🇸🇬 +65</SelectItem>
                      <SelectItem value="+60">🇲🇾 +60</SelectItem>
                      <SelectItem value="+66">🇹🇭 +66</SelectItem>
                      <SelectItem value="+84">🇻🇳 +84</SelectItem>
                      <SelectItem value="+62">🇮🇩 +62</SelectItem>
                      <SelectItem value="+63">🇵🇭 +63</SelectItem>
                      <SelectItem value="+92">🇵🇰 +92</SelectItem>
                      <SelectItem value="+880">🇧🇩 +880</SelectItem>
                      <SelectItem value="+971">🇦🇪 +971</SelectItem>
                      <SelectItem value="+966">🇸🇦 +966</SelectItem>
                      <SelectItem value="+27">🇿🇦 +27</SelectItem>
                      <SelectItem value="+234">🇳🇬 +234</SelectItem>
                      <SelectItem value="+20">🇪🇬 +20</SelectItem>
                      <SelectItem value="+254">🇰🇪 +254</SelectItem>
                      <SelectItem value="+64">🇳🇿 +64</SelectItem>
                      <SelectItem value="+353">🇮🇪 +353</SelectItem>
                      <SelectItem value="+351">🇵🇹 +351</SelectItem>
                      <SelectItem value="+30">🇬🇷 +30</SelectItem>
                      <SelectItem value="+32">🇧🇪 +32</SelectItem>
                      <SelectItem value="+43">🇦🇹 +43</SelectItem>
                      <SelectItem value="+358">🇫🇮 +358</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    id="contact"
                    data-testid="popup-contact-input"
                    type="tel"
                    placeholder="1234567890"
                    maxLength={10}
                    {...register('contact_number')}
                    onKeyPress={(e) => {
                      // Only allow digits
                      if (!/[0-9]/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    onPaste={(e) => {
                      // Prevent pasting non-numeric content
                      const pastedData = e.clipboardData.getData('text');
                      if (!/^\d+$/.test(pastedData)) {
                        e.preventDefault();
                      }
                    }}
                    className="flex-1 text-gray-900 placeholder:text-gray-400 text-sm"
                  />
                </div>
                {errors.contact_number && <p className="text-xs text-red-500 mt-1">{errors.contact_number.message}</p>}
              </div>
              
              <div>
                <Label htmlFor="message" className="text-brand-navy text-xs sm:text-sm">Description (Optional)</Label>
                <Textarea
                  id="message"
                  data-testid="popup-message-input"
                  placeholder="Tell us about your needs..."
                  {...register('message')}
                  className="mt-1 resize-none text-gray-900 placeholder:text-gray-400 text-sm"
                  rows={2}
                />
                {errors.message && <p className="text-xs text-red-500 mt-1">{errors.message.message}</p>}
              </div>
              
              <Button
                type="submit"
                data-testid="popup-submit-button"
                disabled={isSubmitting}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-4 sm:py-6 text-sm sm:text-base transition-transform hover:scale-105"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Request'}
              </Button>
            </form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};